/**
 * Cloudflare Turnstile on staff sign-in (ported from the Hype Forge Base,
 * 2026-10-21; on the code step too since 2026-10-22).
 *
 * The API enforces it on `POST /api/auth/request-link` (action staff_login)
 * and on a code check, `POST /api/auth/verify { email, code }` (action
 * staff_verify), once its TURNSTILE_SITE_KEY var and TURNSTILE_SECRET binding
 * are both set (the Forge sites' shared widget;
 * apps/api/src/staff-turnstile.ts). A token is minted for one action, so the
 * login page runs one widget and switches its action with the step (`use`,
 * which renders a fresh widget; the handle is lib/turnstile-handle.ts, tested
 * in node). The emailed link needs no token. The site key
 * comes from `GET /api/public/config`; with no key nothing loads and the
 * request goes out without a token, exactly as before. If the script cannot
 * load (blocked, offline) the request still goes out and the API decides. The
 * widget's hostname list in the Cloudflare dashboard must cover the admin
 * host. Not the public site's widget: that one is configured in Settings.
 *
 * Browser only; nothing here runs at import time.
 */

import { API_BASE } from './config';
import { STAFF_LOGIN_ACTION, STAFF_VERIFY_ACTION } from './signIn';
import { turnstileHandle, type TurnstileApi, type TurnstileHandle } from './turnstile-handle';

/** The actions the API checks tokens against (apps/api/src/staff-turnstile.ts). */
export { STAFF_LOGIN_ACTION, STAFF_VERIFY_ACTION };
export type { TurnstileHandle };

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loader: Promise<TurnstileApi | null> | undefined;

function api(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

/** Inject the script once. Resolves null when it cannot load. */
function loadTurnstile(): Promise<TurnstileApi | null> {
  loader ??= new Promise<TurnstileApi | null>((resolve) => {
    if (api()) return resolve(api() ?? null);
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => resolve(api() ?? null);
    script.onerror = () => resolve(null);
    document.head.append(script);
  });
  return loader;
}

/** The public Turnstile site key, or null when bot protection is off (or the config cannot be read). */
export async function turnstileSiteKey(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/public/config`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const body = (await res.json()) as { turnstile_site_key?: unknown };
    return typeof body.turnstile_site_key === 'string' && body.turnstile_site_key.trim()
      ? body.turnstile_site_key.trim()
      : null;
  } catch {
    return null;
  }
}

/** Render the widget into `el`. Resolves null when Turnstile is unavailable. */
export async function mountTurnstile(el: HTMLElement, siteKey: string, action: string): Promise<TurnstileHandle | null> {
  const ts = await loadTurnstile();
  return ts ? turnstileHandle(ts, el, siteKey, action) : null;
}
